/**
 * One date format for the product (redesign phase 5, #455, break #132).
 *
 * Always `de-DE`, never the browser's locale: an English browser must not turn
 * a German UI into "10/6/2026". The time zone is the browser's (as today).
 *
 *   formatDate(d)                → 06.10.2026      the default: tables, fields, lists, cards
 *   formatDate(d, "short-month") → 6. Okt. 2026    running text and headers, where dates read as words
 *   formatDateTime(d)            → 06.10.2026, 14:03   (no seconds)
 *   formatTime(d)                → 14:03
 *   formatRelativeDay(d)         → Heute / Gestern / 06.10.2026   wherever a day is named
 *
 * Relative days (Rafi 08.10.2026): "Heute" and "Gestern" for the last two
 * days, everywhere a day is named (lists, day separators, ticket times); older
 * days in the format the site uses anyway (numeric by default, short-month in
 * headings). Times stay "14:03". Nothing beyond yesterday ("vor 3 Tagen") and
 * nothing in the future ("Morgen"): a future date reads as a date.
 *
 * `parseDate` reads what people type into a DatePicker ("6.10.2026",
 * "06.10.2026"); `toIsoDate` / `fromIsoDate` convert the local calendar date to
 * and from `yyyy-mm-dd` for APIs, without the UTC shift `toISOString()` causes
 * (midnight in Berlin is the previous day in UTC).
 */

// Intl formatters are immutable once built; sharing them across calls (and renders) is safe.
const NUMERIC = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
const SHORT_MONTH = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "short", year: "numeric" });
const DATE_TIME = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const TIME = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });

export type DateStyle = "numeric" | "short-month";

export function formatDate(date: Date, style: DateStyle = "numeric"): string {
  return (style === "short-month" ? SHORT_MONTH : NUMERIC).format(date);
}

export function formatDateTime(date: Date): string {
  return DATE_TIME.format(date);
}

export function formatTime(date: Date): string {
  return TIME.format(date);
}

/** A local calendar date, or null when the parts don't form a real one (31.02.). */
function calendarDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  // `new Date` rolls 31.02. over to 03.03.; a round trip catches it.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

/** "T.M.JJJJ" or "TT.MM.JJJJ" → a local date at midnight; anything else → null. No two-digit years. */
export function parseDate(text: string): Date | null {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text.trim());
  if (!match) return null;
  return calendarDate(Number(match[3]), Number(match[2]), Number(match[1]));
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The local calendar date as `yyyy-mm-dd`. */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `yyyy-mm-dd` → a local date at midnight; anything else → null. */
export function fromIsoDate(text: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  return calendarDate(Number(match[1]), Number(match[2]), Number(match[3]));
}

/** Same calendar day, ignoring the time. */
export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Compares calendar days only: negative, 0 or positive. */
export function compareDays(a: Date, b: Date): number {
  return (
    (a.getFullYear() - b.getFullYear()) * 10_000 + (a.getMonth() - b.getMonth()) * 100 + (a.getDate() - b.getDate())
  );
}

export interface RelativeDayLabels {
  /** "Heute". */
  today: string;
  /** "Gestern". */
  yesterday: string;
}

export const DEFAULT_RELATIVE_DAY_LABELS: RelativeDayLabels = { today: "Heute", yesterday: "Gestern" };

export interface RelativeDayOptions {
  /** "Today"; defaults to the current date. */
  now?: Date;
  /** The format for older days (and future ones). Default `numeric`. */
  style?: DateStyle;
  labels?: RelativeDayLabels;
}

/** "Heute", "Gestern", else the date: the one relative-day rule. */
export function formatRelativeDay(date: Date, { now = new Date(), style = "numeric", labels = DEFAULT_RELATIVE_DAY_LABELS }: RelativeDayOptions = {}): string {
  if (isSameDay(date, now)) return labels.today;
  // Day arithmetic through the calendar (setDate rolls months and years over), not 24 h steps: DST days are 23 or 25 h.
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isSameDay(date, yesterday)) return labels.yesterday;
  return formatDate(date, style);
}
